import React, { useState } from 'react';
import axios from 'axios';
import { Calendar, Repeat, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

// Day of week constants
const DAYS_OF_WEEK = [
  { key: 'mon', label: 'M', fullLabel: 'Monday' },
  { key: 'tue', label: 'T', fullLabel: 'Tuesday' },
  { key: 'wed', label: 'W', fullLabel: 'Wednesday' },
  { key: 'thu', label: 'T', fullLabel: 'Thursday' },
  { key: 'fri', label: 'F', fullLabel: 'Friday' },
  { key: 'sat', label: 'S', fullLabel: 'Saturday' },
  { key: 'sun', label: 'S', fullLabel: 'Sunday' }
];

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'every_2_weeks', label: 'Every 2 weeks' },
  { value: 'every_3_weeks', label: 'Every 3 weeks' },
  { value: 'every_4_weeks', label: 'Every 4 weeks' },
  { value: 'monthly', label: 'Monthly' }
];

// Duration options
const DURATION_OPTIONS = [
  { value: '1_month', label: 'One Month', months: 1 },
  { value: '2_months', label: 'Two Months', months: 2 },
  { value: '3_months', label: 'Three Months', months: 3 },
  { value: 'forever', label: 'Forever (12 months)', months: 12 }
];

const AvailabilityModal = ({ calendarId, calendar, selectedSlot, onClose, getAuthHeaders }) => {
  // Check if selected date is today
  const isTodayDate = () => {
    if (!selectedSlot?.date) return false;
    const today = new Date();
    const selected = new Date(selectedSlot.date);
    return today.toDateString() === selected.toDateString();
  };
  
  // Get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes();
    const isFloating = calendar?.booking_type === 'floating';
    
    if (isFloating) {
      const roundedMinutes = Math.ceil(minutes / 15) * 15;
      if (roundedMinutes === 60) {
        return `${(parseInt(hours) + 1).toString().padStart(2, '0')}:00`;
      }
      return `${hours}:${roundedMinutes.toString().padStart(2, '0')}`;
    } else {
      return `${(parseInt(hours) + 1).toString().padStart(2, '0')}:00`;
    }
  };
  
  // Generate time options based on booking type
  const generateTimeOptions = (isStartTime = false) => {
    const options = [];
    const isFloating = calendar?.booking_type === 'floating';
    const currentTime = isStartTime && isTodayDate() ? getCurrentTime() : null;
    
    for (let hour = 0; hour < 24; hour++) {
      if (isFloating) {
        ['00', '15', '30', '45'].forEach(minute => {
          const timeValue = `${hour.toString().padStart(2, '0')}:${minute}`;
          if (!currentTime || timeValue >= currentTime) {
            options.push(timeValue);
          }
        });
      } else {
        const timeValue = `${hour.toString().padStart(2, '0')}:00`;
        if (!currentTime || timeValue >= currentTime) {
          options.push(timeValue);
        }
      }
    }
    
    return options;
  };
  
  // Calculate default end time (1 hour after start time)
  const calculateDefaultEndTime = (startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const isFloating = calendar?.booking_type === 'floating';
    
    if (isFloating) {
      const endHours = (hours + 1) % 24;
      return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
      const endHours = (hours + 1) % 24;
      return `${endHours.toString().padStart(2, '0')}:00`;
    }
  };

  // Get day of week from selected date
  const getSelectedDayOfWeek = () => {
    if (!selectedSlot?.date) return 'mon';
    // Parse date components to avoid timezone issues
    // selectedSlot.date is in format "YYYY-MM-DD"
    const [year, month, day] = selectedSlot.date.split('-').map(Number);
    // Create date using local timezone (month is 0-indexed in JS)
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const mapping = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    return mapping[dayIndex];
  };

  // Calculate initial start time 
  const getInitialStartTime = () => {
    let slotTime = selectedSlot?.time || '09:00';
    
    if (calendar?.booking_type === 'floating') {
      const [hours, minutes] = slotTime.split(':').map(Number);
      const roundedMinutes = Math.round(minutes / 15) * 15;
      if (roundedMinutes === 60) {
        slotTime = `${(hours + 1).toString().padStart(2, '0')}:00`;
      } else {
        slotTime = `${hours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
      }
    }
    
    if (isTodayDate()) {
      const currentTimeStr = getCurrentTime();
      return slotTime >= currentTimeStr ? slotTime : currentTimeStr;
    }
    
    return slotTime;
  };
  
  const startTime = getInitialStartTime();
  const [formData, setFormData] = useState({
    start_time: startTime,
    end_time: calculateDefaultEndTime(startTime),
    full_office_hours: false,
    is_on_hold: true
  });
  
  // Repeat availability state
  const [showRepeatOptions, setShowRepeatOptions] = useState(false);
  const [repeatConfig, setRepeatConfig] = useState({
    enabled: false,
    selectedDays: { [getSelectedDayOfWeek()]: true }, // Default to selected date's day
    frequency: 'weekly',
    duration: '1_month'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setFormData({
      ...formData,
      start_time: newStartTime,
      end_time: formData.full_office_hours ? formData.end_time : calculateDefaultEndTime(newStartTime)
    });
  };

  // Toggle day selection
  const toggleDay = (dayKey) => {
    setRepeatConfig(prev => ({
      ...prev,
      selectedDays: {
        ...prev.selectedDays,
        [dayKey]: !prev.selectedDays[dayKey]
      }
    }));
  };

  // Calculate dates to create based on repeat config
  const calculateRepeatDates = () => {
    if (!repeatConfig.enabled) {
      return [selectedSlot.date];
    }

    const dates = [];
    // Parse date components to avoid timezone issues
    const [year, month, day] = selectedSlot.date.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);
    const durationMonths = DURATION_OPTIONS.find(d => d.value === repeatConfig.duration)?.months || 1;
    const endDate = new Date(year, month - 1 + durationMonths, day);

    // Get selected days as indices (0 = Sunday, 1 = Monday, etc.)
    const dayIndexMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    const selectedDayIndices = Object.entries(repeatConfig.selectedDays)
      .filter(([_, selected]) => selected)
      .map(([dayKey, _]) => dayIndexMap[dayKey]);

    if (selectedDayIndices.length === 0) {
      return [selectedSlot.date]; // Fallback to original date
    }

    // Generate dates
    let currentDate = new Date(startDate);
    let weekCounter = 0;
    const startWeekDay = startDate.getDay(); // Track week start for frequency calculation

    while (currentDate <= endDate) {
      const currentDayIndex = currentDate.getDay();
      
      if (selectedDayIndices.includes(currentDayIndex)) {
        // Check if we should include this week based on frequency
        let includeThisWeek = true;
        if (repeatConfig.frequency === 'every_2_weeks') includeThisWeek = weekCounter % 2 === 0;
        else if (repeatConfig.frequency === 'every_3_weeks') includeThisWeek = weekCounter % 3 === 0;
        else if (repeatConfig.frequency === 'every_4_weeks') includeThisWeek = weekCounter % 4 === 0;
        
        if (includeThisWeek) {
          // Format date as YYYY-MM-DD
          const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
          dates.push(dateStr);
        }
      }
      
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
      
      // Track week changes (when we pass the start day of week)
      if (currentDate.getDay() === startWeekDay) {
        weekCounter++;
      }
    }

    return dates.length > 0 ? dates : [selectedSlot.date];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const datesToCreate = calculateRepeatDates();
      
      // Create availability for each date
      for (const date of datesToCreate) {
        const requestData = {
          calendar_id: calendarId,
          date: date,
          start_time: formData.full_office_hours ? '07:00' : formData.start_time,
          end_time: formData.full_office_hours ? '21:00' : formData.end_time,
          is_available: true,
          is_on_hold: formData.is_on_hold,
          recurrence_type: 'none',
          recurrence_months: 1
        };

        await axios.post(
          `${API_BASE_URL}/availability`,
          requestData,
          { headers: getAuthHeaders() }
        );
      }

      onClose();
    } catch (err) {
      console.error('Error creating availability:', err);
      setError(err.response?.data?.detail || 'Failed to set availability');
    } finally {
      setLoading(false);
    }
  };

  // Count how many dates will be created
  const repeatDatesCount = calculateRepeatDates().length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Set Availability</h2>
            <p className="text-gray-600 mt-1">
              Starting from {selectedSlot?.date}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Time Settings */}
          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.full_office_hours}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  full_office_hours: e.target.checked,
                  start_time: e.target.checked ? '07:00' : formData.start_time,
                  end_time: e.target.checked ? '21:00' : formData.end_time
                })}
                className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500 mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Full Office Hours (7 AM - 9 PM)
              </span>
            </label>

            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.is_on_hold}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  is_on_hold: e.target.checked
                })}
                className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500 mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                On Hold (customers won't see this slot)
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <select
                value={formData.start_time}
                onChange={handleStartTimeChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {generateTimeOptions(true).map(time => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              {isTodayDate() && (
                <p className="text-xs text-gray-500 mt-1">
                  Past times are hidden for today
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <select
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {generateTimeOptions(false)
                  .filter(time => time > formData.start_time)
                  .map(time => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Repeat Availability Section */}
          <div className="border-t pt-4 mt-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={repeatConfig.enabled}
                onChange={(e) => {
                  setRepeatConfig(prev => ({ ...prev, enabled: e.target.checked }));
                  setShowRepeatOptions(e.target.checked);
                }}
                className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500 mr-2"
              />
              <Repeat className="w-4 h-4 mr-2 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">
                Repeat Availability
              </span>
            </label>
            
            {showRepeatOptions && repeatConfig.enabled && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg space-y-4">
                {/* Days of Week */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Which days of the week?
                  </label>
                  <div className="flex gap-1">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day.key}
                        type="button"
                        onClick={() => toggleDay(day.key)}
                        className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                          repeatConfig.selectedDays[day.key]
                            ? 'bg-blue-500 text-white'
                            : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                        title={day.fullLabel}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency?
                  </label>
                  <select
                    value={repeatConfig.frequency}
                    onChange={(e) => setRepeatConfig(prev => ({ ...prev, frequency: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {FREQUENCY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration?
                  </label>
                  <select
                    value={repeatConfig.duration}
                    onChange={(e) => setRepeatConfig(prev => ({ ...prev, duration: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {DURATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Preview */}
                <div className="bg-white rounded p-3 border border-blue-200">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">
                      {repeatDatesCount} availability slot{repeatDatesCount !== 1 ? 's' : ''} will be created
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-md transition-colors disabled:bg-blue-300 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Clock className="w-4 h-4 animate-spin" />
                  Setting...
                </>
              ) : (
                <>Set Availability{repeatConfig.enabled && repeatDatesCount > 1 ? ` (${repeatDatesCount})` : ''}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AvailabilityModal;
